import React, { useState, useRef } from 'react';
import { X, Upload, Loader2, Download, Save, Edit2, CheckCircle, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { saveContactToBackend } from '../lib/contactDownload';
import { getUser } from '../lib/auth';

const BusinessCardScanner = ({ isOpen, onClose, onSave, skipDownload = false }) => {
  const [step, setStep] = useState('upload'); // upload, processing, result
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  const resetScanner = () => {
    setStep('upload');
    setImagePreview(null);
    setExtractedData(null);
    setIsProcessing(false);
    setIsEditing(false);
    setEditedData({});
    stopCamera();
  };

  const handleClose = () => {
    resetScanner();
    onClose();
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera access error:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob(async (blob) => {
      const file = new File([blob], 'captured-card.jpg', { type: 'image/jpeg' });
      stopCamera();
      await processImage(file);
    }, 'image/jpeg', 0.85);
  };

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if too large (max 1200px width)
          const maxWidth = 1200;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          }, 'image/jpeg', 0.85);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const processImage = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    try {
      setIsProcessing(true);
      setStep('processing');

      // Show image preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);

      // Compress image
      toast.loading('Compressing image...', { id: 'ocr-process' });
      const compressedFile = await compressImage(file);

      // Call OCR API
      toast.loading('Extracting contact information...', { id: 'ocr-process' });
      
      const token = localStorage.getItem('auth_token');

      const formData = new FormData();
      formData.append('file', compressedFile);

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/ocr/extract-business-card`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.extracted_data) {
        const data = {
          name: result.extracted_data.name || '',
          businessName: result.extracted_data.company || '',
          title: result.extracted_data.title || '',
          email: result.extracted_data.email || '',
          phone: result.extracted_data.phone || '',
          mobile: result.extracted_data.mobile || '',
          website: result.extracted_data.website || '',
          address: result.extracted_data.address || '',
          linkedin: result.extracted_data.linkedin || '',
        };

        setExtractedData(data);
        setEditedData(data);
        setStep('result');
        toast.success('Contact information extracted!', { id: 'ocr-process' });
      } else {
        throw new Error(result.error_message || 'Failed to extract information');
      }
    } catch (error) {
      console.error('OCR Error:', error);
      toast.error(error.message || 'Failed to process image. Please try again.', { id: 'ocr-process' });
      resetScanner();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    console.log('File selected:', file);
    if (file) {
      console.log('File type:', file.type, 'Size:', file.size);
      processImage(file);
    } else {
      console.log('No file selected');
      toast.error('No file selected. Please try again.');
    }
  };

  const generateVCard = (contact) => {
    const lines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${contact.name || 'Unknown'}`,
      `N:${contact.name || 'Unknown'};;;;`,  // Structured name format
      contact.businessName ? `ORG:${contact.businessName}` : '',
      contact.title ? `TITLE:${contact.title}` : '',
      contact.email ? `EMAIL;TYPE=INTERNET,PREF:${contact.email}` : '',
      contact.phone ? `TEL;TYPE=WORK,VOICE:${contact.phone}` : '',
      contact.mobile ? `TEL;TYPE=CELL,VOICE:${contact.mobile}` : '',
      contact.website ? `URL:${contact.website}` : '',
      contact.address ? `ADR;TYPE=WORK:;;${contact.address};;;;` : '',
      contact.linkedin ? `X-SOCIALPROFILE;TYPE=linkedin:${contact.linkedin}` : ''
    ];
    
    // Add all social links if available
    if (contact.socialLinks && Array.isArray(contact.socialLinks)) {
      contact.socialLinks.forEach(link => {
        if (link.url) {
          const platform = link.platform?.toLowerCase() || 'other';
          if (platform === 'website') {
            lines.push(`URL:${link.url}`);
          } else if (['linkedin', 'facebook', 'twitter', 'instagram', 'youtube', 'tiktok'].includes(platform)) {
            lines.push(`X-SOCIALPROFILE;TYPE=${platform}:${link.url}`);
          } else {
            lines.push(`X-SOCIALPROFILE;TYPE=${platform}:${link.url}`);
          }
        }
      });
    }
    
    lines.push('END:VCARD');
    
    const vcard = lines.filter(line => line && !line.endsWith(':')).join('\r\n');  // RFC 6350 requires CRLF
    
    return vcard;
  };

  const handleSaveAndDownload = async () => {
    // If skipDownload mode (profile editing), just pass data back
    if (skipDownload) {
      toast.success('✅ Card scanned successfully!');
      if (onSave) onSave(editedData);
      handleClose();
      return;
    }
    
    // Normal mode: Download the vCard
    const vcard = generateVCard(editedData);
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${editedData.name?.replace(/\s+/g, '_') || 'contact'}.vcf`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    // Save contact to backend if user is logged in
    const user = getUser();
    let savedToBackend = false;
    
    if (user) {
      try {
        console.log('💾 Saving contact to backend...');
        const result = await saveContactToBackend(editedData, 'card_scanner');
        if (result.success) {
          savedToBackend = true;
          console.log('✅ Contact saved to backend with ID:', result.contactId);
        }
      } catch (error) {
        console.error('Failed to save to backend:', error);
        // Continue even if backend save fails
      }
    }
    
    // Save to localStorage as backup (works for everyone - no login needed)
    try {
      const existingContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      
      // Add unique ID and timestamp
      const contactWithMetadata = {
        ...editedData,
        id: Date.now().toString(),
        scannedAt: new Date().toISOString(),
      };
      
      // Add to beginning of array (newest first)
      existingContacts.unshift(contactWithMetadata);
      
      // Keep only last 100 contacts
      const trimmedContacts = existingContacts.slice(0, 100);
      
      localStorage.setItem('scannedContacts', JSON.stringify(trimmedContacts));
      
      if (savedToBackend) {
        toast.success('✅ Contact saved permanently & vCard downloaded!');
      } else if (user) {
        toast.success('⚠️ Contact saved locally (backend sync failed)');
      } else {
        toast.success('✅ Contact saved & vCard downloaded! Login to save permanently.');
      }
      
      if (onSave) onSave(contactWithMetadata);
    } catch (error) {
      console.error('Save error:', error);
      if (savedToBackend) {
        toast.success('✅ Contact saved & vCard downloaded!');
      } else {
        toast.success('vCard downloaded!');
      }
    }
    
    handleClose();
  };

  if (!isOpen) return null;

  // Check if user is logged in (for save feature)
  const token = localStorage.getItem('auth_token');
  const isLoggedIn = !!token;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-t-2xl sm:rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">Business Card Scanner</h2>
              <p className="text-blue-100 text-xs sm:text-sm mt-1">
                {step === 'upload' ? 'Upload or capture a card' :
                  step === 'processing' ? 'Processing...' :
                  'Review and save'
                }
              </p>
            </div>
            <button
              onClick={handleClose}
              className="hover:bg-white/20 p-2 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {/* Upload Step */}
          {step === 'upload' && !showCamera && (
            <div className="space-y-4 sm:space-y-6">
              <div className="border-2 border-dashed border-gray-300 rounded-xl sm:rounded-2xl p-6 sm:p-12 text-center hover:border-blue-500 transition-colors">
                <div className="flex flex-col items-center gap-3 sm:gap-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center">
                    <Upload className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">
                      Scan Your Business Card
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                      Take a photo or upload an image
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 w-full max-w-md">
                    <button
                      onClick={startCamera}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 sm:py-3 px-4 sm:px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
                    >
                      <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
                      Take Photo
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full bg-white hover:bg-gray-50 text-blue-600 border-2 border-blue-600 py-3 sm:py-3 px-4 sm:px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
                    >
                      <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                      Upload Image
                    </button>
                  </div>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          )}

          {/* Camera Step */}
          {step === 'upload' && showCamera && (
            <div className="space-y-4">
              <div className="relative bg-black rounded-xl overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-auto"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={capturePhoto}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Capture
                </button>
                <button
                  onClick={stopCamera}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 px-6 rounded-xl font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Processing Step */}
          {step === 'processing' && (
            <div className="space-y-4 sm:space-y-6">
              {imagePreview && (
                <div className="rounded-xl sm:rounded-2xl overflow-hidden border-2 border-gray-200">
                  <img src={imagePreview} alt="Business card" className="w-full h-auto max-h-64 object-contain" />
                </div>
              )}
              <div className="flex flex-col items-center gap-3 sm:gap-4 py-6 sm:py-8">
                <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 animate-spin" />
                <div className="text-center">
                  <p className="text-base sm:text-lg font-semibold text-gray-900">Processing Image</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Extracting contact information...</p>
                </div>
              </div>
            </div>
          )}

          {/* Result Step */}
          {step === 'result' && (
            <div className="space-y-4 sm:space-y-6">
              {imagePreview && (
                <div className="rounded-xl sm:rounded-2xl overflow-hidden border-2 border-gray-200">
                  <img src={imagePreview} alt="Business card" className="w-full h-auto max-h-32 sm:max-h-48 object-cover" />
                </div>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm sm:text-base font-semibold text-green-900">Information Extracted!</p>
                  <p className="text-xs sm:text-sm text-green-700">Review and edit if needed</p>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Contact Info</h3>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                  >
                    <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    {isEditing ? 'Done' : 'Edit'}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {[
                    { key: 'name', label: 'Name', icon: '👤' },
                    { key: 'businessName', label: 'Company', icon: '🏢' },
                    { key: 'title', label: 'Title', icon: '💼' },
                    { key: 'email', label: 'Email', icon: '📧' },
                    { key: 'phone', label: 'Phone', icon: '📱' },
                    { key: 'mobile', label: 'Mobile', icon: '📲' },
                    { key: 'website', label: 'Website', icon: '🌐' },
                    { key: 'address', label: 'Address', icon: '📍' },
                  ].map((field) => (
                    <div key={field.key} className="space-y-1">
                      <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-1 sm:gap-2">
                        <span className="text-sm sm:text-base">{field.icon}</span>
                        {field.label}
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedData[field.key] || ''}
                          onChange={(e) => setEditedData({ ...editedData, [field.key]: e.target.value })}
                          className="w-full px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                      ) : (
                        <div className="px-2 sm:px-3 py-2 bg-gray-50 rounded-lg text-gray-900 text-sm sm:text-base break-words">
                          {editedData[field.key] || <span className="text-gray-400 text-xs sm:text-sm">Not available</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:gap-3 pt-4">
                <button
                  onClick={handleSaveAndDownload}
                  className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white py-4 px-6 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg text-base sm:text-lg"
                >
                  {skipDownload ? (
                    <>
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                      Apply to Profile
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 sm:w-6 sm:h-6" />
                      Save & Download vCard
                    </>
                  )}
                </button>
                <button
                  onClick={resetScanner}
                  className="w-full bg-white hover:bg-gray-50 text-gray-900 py-3 px-4 sm:px-6 rounded-xl font-semibold border-2 border-gray-300 transition-colors text-sm sm:text-base"
                >
                  Scan Another Card
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessCardScanner;
